/* Native Impact gallery adapter. Load once after theme.js on a main PDP. */
(() => {
  if (window.__ptProductMedia) return;
  window.__ptProductMedia = true;

  let state;
  let queued = false;
  let pendingSelection = false;
  const mainSelector = '.shopify-section--main-product';

  function id(value) {
    return value == null ? null : String(value);
  }

  function configFor() {
    const script = document.querySelector('script[data-pt-product-media-config]');
    if (!script) return null;
    try {
      const data = JSON.parse(script.textContent);
      if (!data || !Array.isArray(data.variants) || !Array.isArray(data.media) || !Array.isArray(data.sameArtAlternates)) return null;
      data.configText = script.textContent;
      return data;
    } catch (_) {
      return null;
    }
  }

  function positions(value, size) {
    try {
      const parsed = JSON.parse(value);
      if (!Array.isArray(parsed)) return null;
      const numbers = parsed.map(Number);
      return numbers.every(n => Number.isInteger(n) && n >= 1 && n <= size) ? numbers : null;
    } catch (_) {
      return null;
    }
  }

  function findInFragment(root, selector) {
    if (!root || typeof root.querySelector !== 'function') return null;
    const direct = root.querySelector(selector);
    if (direct) return direct;
    for (const template of root.querySelectorAll('template')) {
      const found = findInFragment(template.content, selector);
      if (found) return found;
    }
    return null;
  }

  function closeState() {
    if (!state) return;
    restoreArtwork(state);
    restoreNativeFilter(state);
    state.form?.removeEventListener('product:rerender', state.onRerender, true);
    state.gallery.removeEventListener('carousel:filter', state.onNativeFilter, true);
    state.gallery.removeEventListener('control:filter', state.onNativeFilter, true);
    state = undefined;
    pendingSelection = false;
  }

  const artworkAttributes = ['src', 'srcset', 'sizes', 'width', 'height', 'style'];

  function refreshSelectedHeight(s, slide) {
    if (!slide?.isConnected || !s.carousel.isConnected || !s.carousel.hasAttribute('adaptive-height') || s.carousel.selectedSlide !== slide || slide.getAttribute('data-media-type') !== 'image') return;
    // Native settle clears the old max-height before measuring the selected slide.
    // An image selection cannot start media playback in MediaCarousel's listener.
    s.carousel.dispatchEvent(new CustomEvent('carousel:settle', {
      bubbles: true, detail: { slide, index: s.slides.indexOf(slide) }
    }));
  }

  function restoreNativeFilter(s) {
    if (!s.gallery.isConnected || !s.carousel.isConnected) return;
    const base = [...s.base].sort((a, b) => a - b);
    s.gallery.setAttribute('filtered-indexes', JSON.stringify(base));
    const filteredIndexes = base.map(index => index - 1);
    s.applying = true;
    try {
      s.gallery.querySelectorAll('media-carousel').forEach(carousel => carousel.dispatchEvent(new CustomEvent('carousel:filter', { detail: { filteredIndexes } })));
      s.gallery.querySelectorAll('page-dots').forEach(dots => dots.dispatchEvent(new CustomEvent('control:filter', { detail: { filteredIndexes } })));
    } finally {
      s.applying = false;
    }
  }

  function restoreArtwork(s) {
    s.artworkGeneration++;
    const selected = s.carousel.selectedSlide;
    let restoredSelected = false;
    for (const [image, original] of s.originalArtwork) {
      if (!image.isConnected) continue;
      for (const name of artworkAttributes) {
        if (original[name] === null) image.removeAttribute(name);
        else image.setAttribute(name, original[name]);
      }
      if (selected?.querySelector?.(':scope > img') === image) restoredSelected = true;
    }
    s.originalArtwork.clear();
    s.artworkKey = null;
    if (restoredSelected) refreshSelectedHeight(s, selected);
  }

  function squareSource(s, selected) {
    if (s.data.artworkFormat !== 'square' || selected < 0 || !Array.isArray(s.data.squareArtwork)) return null;
    const media = s.data.media[selected];
    const source = s.data.squareArtwork.find(item => id(item.mediaId) === id(media.id));
    if (!source || !/^(?:https?:)?\/\//.test(source.src) || !Number.isInteger(source.width) || source.width < 1 || source.width !== source.height) return null;
    return source;
  }

  function syncArtwork(s, selected) {
    const source = squareSource(s, selected);
    if (!source) {
      if (s.artworkKey) restoreArtwork(s);
      return;
    }
    const slide = selected >= 0 ? s.slides[selected] : null;
    const image = slide?.querySelector?.(':scope > img');
    const thumb = s.gallery.querySelectorAll('.product-gallery__thumbnail')[selected]?.querySelector?.('img');
    const key = source && image ? `${selected}:${source.src}` : null;
    if (key === s.artworkKey && (s.originalArtwork.size || s.artworkPending || s.artworkFailed)) return;
    if (s.artworkFailed === key) {
      if (s.artworkKey !== key) restoreArtwork(s);
      return;
    }
    restoreArtwork(s);
    if (!key || !image || !slide.isConnected) return;
    s.artworkKey = key;
    s.artworkPending = true;
    const generation = s.artworkGeneration;
    const preload = new Image();
    preload.onload = () => {
      if (state !== s || generation !== s.artworkGeneration || s.artworkKey !== key || !s.gallery.isConnected || !slide.isConnected || slide.querySelector(':scope > img') !== image || id(s.data.media[selected].id) !== slide.getAttribute('data-media-id')) return;
      s.artworkPending = false;
      for (const target of [image, thumb].filter(Boolean)) {
        s.originalArtwork.set(target, Object.fromEntries(artworkAttributes.map(name => [name, target.getAttribute(name)])));
        target.setAttribute('srcset', `${source.src} ${source.width}w`);
        target.setAttribute('sizes', target === thumb ? '(max-width: 699px) 56px, 64px' : (target.getAttribute('sizes') || '100vw'));
        target.setAttribute('width', String(source.width));
        target.setAttribute('height', String(source.height));
        target.style.aspectRatio = '1 / 1';
        target.setAttribute('src', source.src);
      }
      refreshSelectedHeight(s, slide);
    };
    preload.onerror = () => {
      if (state !== s || generation !== s.artworkGeneration || s.artworkKey !== key) return;
      s.artworkPending = false;
      s.artworkFailed = key;
      restoreArtwork(s);
    };
    preload.src = source.src;
  }

  function bind() {
    const data = configFor();
    const section = data && document.querySelector(mainSelector);
    const gallery = section?.querySelector('product-gallery');
    const formId = gallery?.getAttribute('form');
    const form = formId && document.getElementById(formId);
    if (!data || !gallery || !formId || !formId.startsWith(`product-form-${data.productId}-`) ||
        (form && (!section.contains(form) || form.getAttribute('id') !== formId ||
          (form.matches && !form.matches('form[is="product-form"], form[hidden]'))))) {
      closeState();
      return;
    }
    if (state?.gallery === gallery && state.form === form && state.data.configText === data.configText && state.slides.every((slide, index) => slide === gallery.querySelector('media-carousel')?.querySelectorAll('.product-gallery__media')[index])) return;
    const previous = state;
    closeState();
    const carousel = gallery.querySelector('media-carousel');
    if (!carousel) return;
    if (typeof carousel.select !== 'function') {
      if (typeof customElements !== 'undefined') customElements.whenDefined('media-carousel').then(schedule);
      return;
    }
    const slides = Array.from(carousel.querySelectorAll('.product-gallery__media'));
    const base = positions(gallery.getAttribute('filtered-indexes'), slides.length);
    const exactMedia = slides.length === data.media.length && data.media.every((media, index) =>
      id(media.id) === slides[index].getAttribute('data-media-id') && Number(media.position) === index + 1);
    // Free-tool signups have no product form. Only a complete, static one-variant
    // native gallery can be handled without a form or a variant event source.
    if (!base || !exactMedia || (!form && data.variants.length !== 1)) { closeState(); return; }
    const next = {
      section, gallery, form, carousel, slides, data, base,
      variantId: id(form?.querySelector('input[name="id"]')?.value || data.selectedVariantId), applying: false,
      originalArtwork: new Map(), artworkGeneration: 0, artworkKey: null, artworkPending: false, artworkFailed: null
    };
    const initialVariant = data.variants.find(variant => id(variant.id) === next.variantId);
    const fallbackId = !initialVariant?.featuredMediaId && id(initialVariant?.fallbackMediaId);
    // A verified fallback needs one initial native selection. Keep the visitor's
    // later carousel choice when the same gallery/configuration binds again.
    pendingSelection = !!fallbackId && (!previous || previous.gallery !== gallery ||
      previous.variantId !== next.variantId ||
      id(previous.data.variants.find(variant => id(variant.id) === next.variantId)?.fallbackMediaId) !== fallbackId);
    next.onRerender = event => {
      const markup = findInFragment(event.detail?.htmlFragment, `product-gallery[form="${gallery.getAttribute('form')}"]`);
      const fresh = markup && positions(markup.getAttribute('filtered-indexes'), slides.length);
      // If the incoming product/gallery cannot be checked, discard our old filter.
      next.base = fresh || [];
      schedule();
    };
    next.onNativeFilter = event => {
      if (next.applying) return;
      const indexes = event.detail?.filteredIndexes;
      if (!Array.isArray(indexes) || !indexes.every(n => Number.isInteger(n) && n >= 0 && n < slides.length)) return;
      next.base = indexes.map(n => n + 1);
      schedule();
    };
    form?.addEventListener('product:rerender', next.onRerender, true);
    gallery.addEventListener('carousel:filter', next.onNativeFilter, true);
    gallery.addEventListener('control:filter', next.onNativeFilter, true);
    state = next;
    apply();
  }

  function artworkFilter(s) {
    const { data, slides } = s;
    if (data.media.length !== slides.length) return { hidden: [], selected: -1 };
    if (!data.media.every((media, index) => id(media.id) === slides[index].getAttribute('data-media-id') && Number(media.position) === index + 1)) {
      return { hidden: [], selected: -1 };
    }
    const variants = data.variants;
    const selectedVariant = variants.find(variant => id(variant.id) === s.variantId);
    let selectedId = id(selectedVariant?.featuredMediaId || selectedVariant?.fallbackMediaId);
    const defaultId = id(data.defaultArtworkMediaId);
    if (!selectedId && defaultId && data.variants.length === 1 && selectedVariant) {
      const alternates = data.sameArtAlternates.filter(item => id(item.artworkMediaId) === defaultId);
      if (data.media.some(media => id(media.id) === defaultId && media.type === 'image') &&
          alternates.every(item => data.media.some(media => id(media.id) === id(item.mediaId) && media.type === 'image'))) selectedId = defaultId;
    }
    const selected = data.media.findIndex(media => id(media.id) === selectedId && media.type === 'image');
    // A missing current association cannot identify the opposite artwork safely.
    if (selected < 0) return { hidden: [], selected: -1 };
    const artworkIds = new Set(variants.map(variant => id(variant.featuredMediaId || variant.fallbackMediaId)).filter(Boolean));
    artworkIds.add(selectedId);
    const hidden = [];
    data.media.forEach((media, index) => {
      if (media.type === 'image' && artworkIds.has(id(media.id)) && id(media.id) !== selectedId) hidden.push(index + 1);
    });
    // Optional exact-ID map only: each entry identifies an alternate and its
    // verified associated variant artwork. Unmatched media stays shared.
    for (const alternate of data.sameArtAlternates) {
      const owner = id(alternate.artworkMediaId);
      const index = data.media.findIndex(media => id(media.id) === id(alternate.mediaId) && media.type === 'image');
      if (index < 0 || !artworkIds.has(owner) || id(data.media[index].id) === selectedId) continue;
      // Keep the canonical variant artwork, never both copies of the same art.
      hidden.push(index + 1);
    }
    return { hidden, selected };
  }

  function apply() {
    const s = state;
    if (!s || !s.gallery.isConnected || !s.section.isConnected) return;
    const { hidden, selected } = artworkFilter(s);
    const filtered = new Set([...s.base, ...hidden]);
    // Keep Impact's real alt-based native filter, including when it excludes the
    // selected canonical slide. Only remove our own artwork filter from it.
    const oneBased = [...filtered].sort((a, b) => a - b);
    const current = positions(s.gallery.getAttribute('filtered-indexes'), s.slides.length);
    if (!current || current.join(',') !== oneBased.join(',')) {
      s.gallery.setAttribute('filtered-indexes', JSON.stringify(oneBased));
      const zeroBased = oneBased.map(n => n - 1);
      s.applying = true;
      try {
        s.gallery.querySelectorAll('media-carousel').forEach(carousel => carousel.dispatchEvent(new CustomEvent('carousel:filter', { detail: { filteredIndexes: zeroBased } })));
        s.gallery.querySelectorAll('page-dots').forEach(dots => dots.dispatchEvent(new CustomEvent('control:filter', { detail: { filteredIndexes: zeroBased } })));
      } finally {
        s.applying = false;
      }
    }
    // Impact's select() owns scroll, next/previous, and carousel:change -> dots.
    // Re-select only for an actual edition change or if the old slide is hidden.
    if (selected >= 0 && !s.base.includes(selected + 1) && (pendingSelection || s.carousel.selectedSlide?.hidden)) {
      s.carousel.select(selected, { animate: false });
    }
    pendingSelection = false;
    syncArtwork(s, selected);
  }

  function schedule() {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      bind();
      apply();
    });
  }

  document.addEventListener('variant:change', event => {
    const s = state;
    if (!s || event.target !== s.form || !event.detail?.variant) return;
    const nextId = id(event.detail.variant.id);
    if (nextId !== s.variantId) pendingSelection = true;
    s.variantId = nextId;
    schedule();
  });
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:unload', schedule);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
  else bind();
})();
